import { appConfig } from '@/config';

let identity = { userId: '', token: '' };
let listenersReady = false;
let registrationPromise = null;
let registrationResult = null;

export function nativePushSupported() {
  return Boolean(window.Capacitor?.isNativePlatform?.() && window.Capacitor?.getPlatform?.() === 'android');
}

async function request(action, token, fcmToken = '') {
  const response = await fetch(new URL('/business-api/fcm-devices.php', appConfig.serviceOrigin), {
    method: 'POST', credentials: 'omit', cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, token, fcm_token: fcmToken }),
  });
  const body = await response.text();
  let data;
  try {
    data = JSON.parse(body);
  } catch {
    throw new Error(`通知服务返回了无效响应（HTTP ${response.status}），请稍后刷新状态或联系管理员。`);
  }
  if (!response.ok || !data.ok) throw new Error(data.message || 'FCM 设备绑定失败');
  return data;
}

function waitForRegistration(timeoutMs = 20000) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('FCM 注册超时，请检查网络后重试。')), timeoutMs);
    registrationResult = {
      resolve: value => { clearTimeout(timer); registrationResult = null; resolve(value); },
      reject: error => { clearTimeout(timer); registrationResult = null; reject(error); },
    };
  });
}

async function register(owner) {
  if (!owner.userId || !owner.token || !nativePushSupported()) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === 'prompt') permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') return;
  await PushNotifications.createChannel({
    id: 'ezlife_messages', name: '消息通知', description: 'EzLife 新消息提醒',
    importance: 5, sound: 'default', vibration: true,
  });
  if (!listenersReady) {
    listenersReady = true;
    await PushNotifications.addListener('registration', async ({ value }) => {
      const current = identity;
      if (!current.userId || !current.token || !value) {
        registrationResult?.reject(new Error('请先登录后开启消息通知。'));
        return;
      }
      try {
        await request('register', current.token, value);
        registrationResult?.resolve(value);
      } catch (error) {
        console.error('FCM token registration failed', error);
        registrationResult?.reject(error instanceof Error ? error : new Error('FCM 设备绑定失败，请稍后重试。'));
      }
    });
    await PushNotifications.addListener('registrationError', error => {
      console.error('FCM registration failed', error);
      const detail = String(error?.message || error?.error || error?.code || '未知错误').slice(0, 240);
      registrationResult?.reject(new Error(`FCM 注册失败：${detail}`));
    });
    await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      const data = notification?.data || {};
      if (data.user_id && data.user_id === identity.userId && data.conversation_id) {
        const url = new URL(location.href);
        url.searchParams.set('pushUser', data.user_id);
        url.searchParams.set('pushConversation', data.conversation_id);
        location.assign(url.href);
      }
    });
  }
  if (registrationPromise) await registrationPromise;
  const registered = waitForRegistration();
  registrationPromise = PushNotifications.register();
  try {
    await registrationPromise;
    await registered;
  } finally {
    registrationPromise = null;
  }
}

export async function restoreNativePushSession(next) {
  const previous = identity;
  identity = { userId: next.userId || '', token: next.token || '' };
  if (!nativePushSupported()) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  if (!identity.userId) {
    if (previous.userId && previous.token) await request('unregister', previous.token);
    return;
  }
  const permission = await PushNotifications.checkPermissions();
  if (permission.receive === 'granted') await register(identity);
}

export async function nativePushStatus() {
  if (!nativePushSupported()) return { supported: false, permission: 'unsupported', enabled: false };
  const { PushNotifications } = await import('@capacitor/push-notifications');
  const permission = await PushNotifications.checkPermissions();
  if (!identity.userId || !identity.token) return { supported: true, permission: permission.receive, enabled: false };
  const data = await request('status', identity.token);
  return { supported: true, permission: permission.receive, enabled: permission.receive === 'granted' && Boolean(data.enabled) };
}

export async function enableNativePush() {
  if (!nativePushSupported()) throw new Error('当前客户端不是 EzLife Android 应用。');
  if (!identity.userId || !identity.token) throw new Error('请先登录后开启消息通知。');
  const { PushNotifications } = await import('@capacitor/push-notifications');
  let permission = await PushNotifications.checkPermissions();
  if (permission.receive !== 'granted') permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') throw new Error('通知权限未允许，请到安卓系统设置中开启 EzLife 通知。');
  await register(identity);
  return nativePushStatus();
}

export async function disableNativePush() {
  if (!identity.userId || !identity.token) throw new Error('请先登录后操作。');
  await request('unregister', identity.token);
  return nativePushStatus();
}

export async function testNativePush() {
  if (!nativePushSupported()) throw new Error('当前客户端不是 EzLife Android 应用。');
  if (!identity.userId || !identity.token) throw new Error('请先登录后测试通知。');
  await request('test', identity.token);
}
